import { Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'
import { FirestoreTosite, MUU_MAKSUTAPA_ID, MYYNTILASKU_MAKSUTAPA_ID, MYYNTITOSITE_MAKSUTAPA_ID, PALKKATOSITE_MAKSUTAPA_ID, SAHKOISET_LASKUT_MAKSUTAPA_ID, TILIOTETOSITE_MAKSUTAPA_ID, TositeTyyppi } from '@lemon/core-firebase/model/tosite'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { BehaviorSubject, combineLatest, Observable, of as observableOf } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { ProjektitService } from '../../../_jaettu-angular/projektit.service'
import { FirebaseLemonaid } from '../firebase-lemonaid.service'
import { KayttajaService } from '../kayttaja.service'

export interface VuosiKk {
  vuosi: number
  kk: number
}

export interface Hakukriteerit {
  tositeTyyppi: TositeTyyppi | null
  vuosikk: VuosiKk
}

@Injectable()
export class TositeDatasourceUusiService {
  private oletuskriteerit: Hakukriteerit = {
    vuosikk: {
      kk: new Date().getMonth(),
      vuosi: new Date().getFullYear(),
    },
    tositeTyyppi: null,
  }
  private hakukriteeritSubject = new BehaviorSubject<Hakukriteerit>(this.oletuskriteerit)
  public hakukriteeritObservable: Observable<Hakukriteerit> = this.hakukriteeritSubject.asObservable()

  private lataaSubject = new BehaviorSubject<boolean>(true)
  public lataaObservable: Observable<boolean> = this.lataaSubject.asObservable()

  private _valittuMaksutapaSubject = new BehaviorSubject<number | null>(null)
  public valittuMaksutapaObservable: Observable<number | null> = this._valittuMaksutapaSubject.asObservable()

  private internalKuititObservable: Observable<FirestoreTosite[]>
  public kuukaudenKuititObservable: Observable<FirestoreTosite[]>
  public suodatetutKuititObservable: Observable<FirestoreTosite[]>
  public kuititObservable: Observable<FirestoreTosite[]>
  public rivienMaaraObservable: Observable<number>

  private pageEventSubject = new BehaviorSubject<PageEvent>({
    length: 0,
    pageIndex: 0,
    pageSize: 20,
  })

  private sortSubject = new BehaviorSubject<Sort>({
    active: 'pvm',
    direction: 'desc',
  })

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _kayttajaService: KayttajaService,
    private _tositeUriService: TositeUriService,
    private _dateService: DateService,
    private _timestampService: TimestampService,
    private _projektitService: ProjektitService,
  ) {
    this.internalKuititObservable = combineLatest([this.hakukriteeritObservable, this._kayttajaService.kayttajanTiedotObservable]).pipe(
      tap(() => {
        this.lataaSubject.next(true)
      }),
      switchMap(([hakukriteerit, kayttajanTiedot]) => {
        if (!kayttajanTiedot) {
          return observableOf<FirestoreTosite[]>([])
        }

        const kuukaudenAlku = new Date(hakukriteerit.vuosikk.vuosi, hakukriteerit.vuosikk.kk, 1)
        const kuukaudenAlkuTs = this._timestampService.dateToTimestamp(this._dateService.kuukaudenEnsimmainen(kuukaudenAlku))
        const kuukaudenLoppuTs = this._timestampService.dateToTimestamp(this._dateService.kuukaudenViimeinen(kuukaudenAlku))

        const kuittiUri = this._tositeUriService.annaTositteidenFirestoreCollectionUri(kayttajanTiedot.asiakasAvain)

        const q = this._firebaseLemonaid.firestoreCollection<FirestoreTosite>(kuittiUri).whereFree('pvm', '>=', kuukaudenAlkuTs).whereFree('pvm', '<=', kuukaudenLoppuTs)

        return combineLatest([q.listen(), this._projektitService.naytettavatKirjanpitoprojektitObservable, this.sortSubject]).pipe(
          map(([snapshots, projektit, sort]) => {
            let suodatetut = snapshots.filter(snap => this._onkoListattava(snap))

            if (hakukriteerit.tositeTyyppi) {
              suodatetut = suodatetut.filter(this.valitseTositeTyypinMukaan(hakukriteerit.tositeTyyppi))
            }
            const projektiNimet = new Map((projektit ?? []).map(p => [p.avain, p.nimi]))
            return this.sorttaa(suodatetut, sort, projektiNimet)
          }),
          tap(() => {
            this.lataaSubject.next(false)
          }),
        )
      }),
      lemonShare(),
    )

    this.kuukaudenKuititObservable = combineLatest([this.internalKuititObservable, this.lataaObservable]).pipe(map(([kuitit, lataa]) => (lataa ? [] : kuitit)))

    this.suodatetutKuititObservable = combineLatest([this.kuukaudenKuititObservable, this.valittuMaksutapaObservable]).pipe(
      map(([kuitit, maksutapa]) => (maksutapa === null ? kuitit : kuitit.filter(kuitti => kuitti.maksutapa === maksutapa))),
      lemonShare(),
    )

    this.rivienMaaraObservable = this.suodatetutKuititObservable.pipe(map(kuitit => kuitit.length))

    this.kuititObservable = combineLatest([this.suodatetutKuititObservable, this.pageEventSubject]).pipe(
      map(([kuitit, page]) => {
        const alku = page.pageIndex * page.pageSize
        const loppu = alku + page.pageSize
        return kuitit.slice(alku, loppu)
      }),
    )
  }

  private sorttaa(tositteet: FirestoreTosite[], sort: Sort, projektiNimet: Map<string, string>): FirestoreTosite[] {
    const kerroin = sort.direction === 'asc' ? 1 : -1

    return [...tositteet].sort((a, b) => {
      let tulos = 0
      if (sort.active === 'toimittaja') {
        tulos = this.vertaaToimittajat(a, b) * kerroin
      } else if (sort.active === 'summa') {
        tulos = this.vertaaSummat(a, b) * kerroin
      } else if (sort.active === 'maksutapa') {
        tulos = this.vertaaMaksutavat(a, b) * kerroin
      } else if (sort.active === 'projekti') {
        tulos = this.vertaaProjektit(a, b, projektiNimet) * kerroin
      } else {
        tulos = this.vertaaPvmt(a, b) * kerroin
      }
      if (tulos !== 0) {
        return tulos
      }

      tulos = this.vertaaPvmt(b, a)
      if (tulos !== 0) {
        return tulos
      }
      tulos = this.vertaaToimittajat(a, b)
      if (tulos !== 0) {
        return tulos
      }
      return this.vertaaSummat(a, b)
    })
  }

  private _onkoListattava(tosite: FirestoreTosite): boolean {
    if (tosite.x || tosite.poistettu === true || tosite.type === 'payment') {
      return false
    }
    if (tosite.type === 'einvoice' && tosite.maksutapa !== SAHKOISET_LASKUT_MAKSUTAPA_ID) {
      return false
    }
    if (tosite.einvoiceApprovalStatus === 'rejected' || tosite.einvoiceApprovalStatus === 'pending-approval') {
      return false
    }
    // Uuden vastaanoton kautta tullut lasku näytetään vasta, kun sille on merkitty maksupäivä
    if (tosite.paymentStatus && !tosite.paymentDate) {
      return false
    }
    return true
  }

  private valitseTositeTyypinMukaan(tyyppi: TositeTyyppi): (tosite: FirestoreTosite) => boolean {
    switch (tyyppi) {
      case 'osto':
        return tosite => ![MYYNTITOSITE_MAKSUTAPA_ID, PALKKATOSITE_MAKSUTAPA_ID, TILIOTETOSITE_MAKSUTAPA_ID, MUU_MAKSUTAPA_ID, MYYNTILASKU_MAKSUTAPA_ID].includes(tosite.maksutapa)
      case 'myynti':
        return tosite => tosite.maksutapa === MYYNTITOSITE_MAKSUTAPA_ID
      case 'palkka':
        return tosite => tosite.maksutapa === PALKKATOSITE_MAKSUTAPA_ID
      case 'tiliote':
        return tosite => tosite.maksutapa === TILIOTETOSITE_MAKSUTAPA_ID
      default:
        return () => true
    }
  }

  get page(): PageEvent {
    return this.pageEventSubject.value
  }

  getSort() {
    return this.sortSubject.value
  }

  changePage(pageEvent: PageEvent): void {
    this.pageEventSubject.next(pageEvent)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  changeSort(sort: Sort): void {
    this.sortSubject.next(sort)
  }

  getVuosiKk(): VuosiKk {
    return this.hakukriteeritSubject.value.vuosikk
  }

  changeVuosiKk(vuosi: number, kk: number): void {
    const nykyisetArvot = this.hakukriteeritSubject.value
    if (nykyisetArvot.vuosikk.vuosi === vuosi && nykyisetArvot.vuosikk.kk === kk) {
      return
    }
    this.hakukriteeritSubject.next({ ...nykyisetArvot, vuosikk: { vuosi, kk } })
    this._resetoiSivutus()
  }

  getValittuMaksutapa(): number | null {
    return this._valittuMaksutapaSubject.value
  }

  vaihdaValittuMaksutapa(maksutapa: number | null): void {
    if (this._valittuMaksutapaSubject.value === maksutapa) {
      return
    }
    this._valittuMaksutapaSubject.next(maksutapa)
    this._resetoiSivutus()
  }

  changeTositeTyyppi(tositeTyyppi: TositeTyyppi | null): void {
    const nykyisetArvot = this.hakukriteeritSubject.value
    if (nykyisetArvot.tositeTyyppi === tositeTyyppi) {
      return
    }
    this.hakukriteeritSubject.next({ ...nykyisetArvot, tositeTyyppi })
    this._resetoiSivutus()
  }

  private _resetoiSivutus(): void {
    const nykyinenPage = this.pageEventSubject.value
    if (nykyinenPage.pageIndex === 0) {
      return
    }
    this.pageEventSubject.next({ ...nykyinenPage, pageIndex: 0 })
  }

  private vertaaPvmt(a: FirestoreTosite, b: FirestoreTosite): number {
    const pvmA = this._dateService.localDateToNumber(a.localPvm) ?? 0
    const pvmB = this._dateService.localDateToNumber(b.localPvm) ?? 0
    return pvmA - pvmB
  }

  private vertaaToimittajat(a: FirestoreTosite, b: FirestoreTosite): number {
    return (a.seller ?? a.el ?? '').localeCompare(b.seller ?? b.el ?? '', 'fi')
  }

  private vertaaSummat(a: FirestoreTosite, b: FirestoreTosite): number {
    return (a.sum ?? 0) - (b.sum ?? 0)
  }

  private vertaaMaksutavat(a: FirestoreTosite, b: FirestoreTosite): number {
    return (a.maksutapa ?? 0) - (b.maksutapa ?? 0)
  }

  private vertaaProjektit(a: FirestoreTosite, b: FirestoreTosite, projektiNimet: Map<string, string>): number {
    const nimiA = projektiNimet.get(a.projektiAvain ?? '') ?? ''
    const nimiB = projektiNimet.get(b.projektiAvain ?? '') ?? ''
    return nimiA.localeCompare(nimiB, 'fi')
  }
}
